import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { WhatsAppBusinessAccount } from '../../../configurations/integration/bots/models/whatsapp';
import { Template } from '../components/whatsapp-template/template-model';

@Injectable({
  providedIn: 'root',
})
export class WhatsAppService {
  private readonly baseUrl: string = '';

  constructor(private http: HttpClient) {
    this.baseUrl = `${environment.whatsAppUrl}`;
  }

  getWhatsAppSubscription(): Observable<any> {
    return this.http.get(`${this.baseUrl}WhatsappBusiness/subscription`);
  }

  alterWhatsAppSubscription(): Observable<any> {
    return this.http.post(
      `${this.baseUrl}WhatsappBusiness/subscription/alter`,
      {}
    );
  }

  completeEmbeddedSignUp(code: string): Observable<any> {
    return this.http.post<any[]>(`${this.baseUrl}EmbeddedSignupFlow`, { code });
  }

  syncBusinessAccounts(): Observable<any> {
    return this.http.post(`${this.baseUrl}WhatsappBusiness/sync-numbers`, {});
  }

  getBusinessAccounts(): Observable<WhatsAppBusinessAccount[]> {
    return this.http.get<WhatsAppBusinessAccount[]>(
      `${this.baseUrl}WhatsappBusiness/phone-numbers`
    );
  }

  registerWhatsAppBusinessPhone(value: any): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}WhatsappBusiness/register-phone`,
      value
    );
  }

  deregisterWhatsAppBusinessPhone(id: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}WhatsappBusiness/phone-numbers/${id}/deregister`,
      {}
    );
  }

  updateWhatsAppBusinessStatus(id: number, value: any): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}WhatsappBusiness/phone-numbers/${id}/status`,
      value
    );
  }

  getMessageTemplates(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}WhatsappTemplate/templates`);
  }

  getTemplatePageList(query = {}): Observable<any> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(query)) {
      params = params.append(key, value as any);
    }
    return this.http.get<any>(
      `${this.baseUrl}WhatsappTemplate/templates-paged`,
      { params }
    );
  }

  syncMessageTemplatesStatus(): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}WhatsappTemplate/templates/sync-status`,
      {}
    );
  }

  createMessageTemplate(templateMsg: Template): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}WhatsappTemplate/templates`,
      templateMsg
    );
  }

  updateMessageTemplate(
    templateId: string,
    templateMsg: Template
  ): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}WhatsappTemplate/templates/${templateId}`,
      templateMsg
    );
  }

  updateWhatsAppTemplateStatus(id: number, value: any): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}WhatsappTemplate/templates/${id}/status`,
      value
    );
  }

  deleteTemplate(templateName: string): Observable<any> {
    const params = new HttpParams().append('templateName', templateName);
    return this.http.delete(`${this.baseUrl}WhatsappTemplate/templates`, {
      params,
    });
  }

  getConversations(query): Observable<any> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(query)) {
      params = params.append(key, value as any);
    }
    return this.http.get(`${this.baseUrl}WhatsappMessage/conversations`, {
      params,
    });
  }

  sentMessage(message: any): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}WhatsappMessage/text-message`,
      message
    );
  }

  sentMediaMessage(message: any): Observable<unknown> {
    const formData = new FormData();
    formData.append('File', message.file, message?.file?.name);
    formData.append('Type', message.type);
    formData.append('PhoneNoId', message.phoneNoId);
    formData.append('RecipientPhoneNo', message.recipientPhoneNo);
    formData.append('Caption', message?.caption ?? '');
    if(message?.messageContext){
      formData.append('MessageContext.MessageId', message?.messageContext);
    }
    return this.http.post<unknown>(
      `${this.baseUrl}WhatsappMessage/media-message`,
      formData
    );
  }

  uploadDocument(file: File): Observable<WADocument> {
    const formData = new FormData();
    formData.append('File', file, file.name);
    return this.http.post<WADocument>(
      `${this.baseUrl}WhatsappDocument/documents`,
      formData
    );
  }

  setTemplateMessage(message: any): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}WhatsappMessage/templated-message`,
      message
    );
  }

  setTemplateMessageNew(message: any): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}WhatsappMessage/TemplatedMessage`,
      message
    );
  }


  resendMessage(whatsAppMessageId: string): Observable<unknown> {
      return this.http.post<unknown>(
          `${this.baseUrl}WhatsappMessage/resend-message`,
          { messageId: whatsAppMessageId }
      );
  }

  getMessages(
    conversationId: number,
    newerThanId?: number | null
  ): Observable<any> {
    let params = new HttpParams();
    params = !!newerThanId ? params.append('newerThanId', newerThanId) : params;
    return this.http.get<any>(
      `${this.baseUrl}WhatsappMessage/${conversationId}/messages`,
      { params }
    );
  }

  // WhatsApp Campaign

  getCampaigns(query = {}): Observable<any> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(query)) {
      params = params.append(key, value as any);
    }
    return this.http.get(`${this.baseUrl}WhatsappCampaign/campaigns`, {
      params,
    });
  }

  getCampaign(id): Observable<any> {
    return this.http.get(`${this.baseUrl}WhatsappCampaign/campaigns/${id}`);
  }

  getCampaignMembers(id): Observable<any> {
    return this.http.get(
      `${this.baseUrl}WhatsappCampaign/campaigns/${id}/members`
    );
  }

  getContacts(module: string, startDateTime: string, endDateTime: string): Observable<any> {
      const params = new HttpParams()
          .append('moduleName', module)
          .append('lastMessageDateTimeStart', startDateTime)
          .append('lastMessageDateTimeEnd', endDateTime);

      return this.http.get(`${this.baseUrl}WhatsappCampaign/contacts`, { params });
  }

  createCampaigns(campaign): Observable<any> {
    return this.http.post(
      `${this.baseUrl}WhatsappCampaign/campaigns`,
      campaign
    );
  }

  updateCampaigns(campaignId, campaign): Observable<any> {
    return this.http.put(
      `${this.baseUrl}WhatsappCampaign/campaigns/${campaignId}`,
      campaign
    );
  }

  deleteCampaigns(campaignId): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}WhatsappCampaign/campaigns/${campaignId}`
    );
  }

  startScheduler(campaignId: number): Observable<any> {
    return this.http.post(
      `${this.baseUrl}WhatsappCampaign/campaigns/${campaignId}/start-schedular`,
      {}
    );
  }

  stopScheduler(campaignId: number): Observable<any> {
    return this.http.post(
      `${this.baseUrl}WhatsappCampaign/campaigns/${campaignId}/stop-schedular`,
      {}
    );
  }

  getCampaignAnalytics(campaignId: number): Observable<any> {
    const params = new HttpParams().append('campaignId', campaignId);
    return this.http.get(`${this.baseUrl}WhatsappCampaign/analytics`, {
      params,
    });
  }

  getHistories(query = {}): Observable<any> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(query)) {
      params = params.append(key, value as any);
    }

    return this.http.get(`${this.baseUrl}WhatsappMessage/histories`, {
      params,
    });
  }

  checkSubscription(phone: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.baseUrl}WhatsappCampaign/subscription/${phone}`
    );
  }

  
  deleteContact(id,associationTypeId): Observable<any> {
    return this.http.delete<any>(`${environment.contactURL}Contact/AssociationType/${associationTypeId}/Contact/${id}`)
}

  emojiReaction(payload): Observable<any> {
    return this.http.post<any>(`${environment.whatsAppUrl}WhatsappMessage/reaction-message`, payload)
  }

  whatsAppChatLastSeenFillter(payload:any): Observable<any>{
    return this.http.post<any>(`${environment.whatsAppUrl}WhatsappMessage/WAChatPagedList`, payload)
  }

  whatsAppChatActivePlans(): Observable<any>{
    return this.http.get<any>(`${environment.appOrgUrl}Payment/ActivePlans`);
  }

  getAllContactSource() {
    return this.http.get<any>(environment.coreUrl + 'Source/SourceMinified')
      .toPromise()
      .then(res => res);
  }

  getAllContactTypes() {
    return this.http.get<any>(`${environment.appOrgUrl}ContactTypeUserPermission/AllContactType`);
  }

  getAllTags() {
      return this.http.get<any>(`${environment.contactURL}Tag`);
  }

  getAllContactTypesPermission(groupId) {
    return this.http.get<any>(`${environment.coreUrl}Type?groupId=${groupId}`);
  }

  getAllContactTypePermissionList(contactTypeId:any) {
    return this.http.get<any>(`${environment.appOrgUrl}ContactTypeUserPermission/ContactType/${contactTypeId}`);
  }

  addContactTypePermission(data:any) {
    return this.http.post<any>(`${environment.appOrgUrl}ContactTypeUserPermission`, data);
  }

  updateContactTypePermission(data:any) {
    return this.http.put<any>(`${environment.appOrgUrl}ContactTypeUserPermission`, data);
  }

  deleteContactTypePermission(contactId:any) {
    return this.http.delete<any>(`${environment.appOrgUrl}ContactTypeUserPermission/${contactId}`);
  }

  getSubscriptionExpiredStatus() {
    return this.http.get<any>(`${environment.appOrgUrl}Payment/GetSubscriptionUserMapStatus`);
  }

  getFlowList(data): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}WhatsAppFlowMessage/FlowPagedList`, data);
  }

  syncFlow(): Observable<any>{
    return this.http.post<any>(`${this.baseUrl}WhatsAppFlowMessage/Flows/Sync`, {});
  }
  getFlowDropDown(){
    return this.http.get<any>(`${environment.whatsAppUrl}WhatsAppFlowMessage/Flows`);
  }

  getFlowDetails(flowId){
    return this.http.get<any>(`${environment.whatsAppUrl}WhatsAppFlowMessage/Flow/${flowId}`);
  }

  updateFlow(data){
    return this.http.put<any>(`${environment.whatsAppUrl}WhatsAppFlowMessage/UpdateFlow`, data);
  }

  sendFlowOnMessenger(data){
    return this.http.post<any>(`${environment.whatsAppUrl}WhatsAppFlowMessage/flow_message`,data);
  }

  getTemplateDetailById(id){
    return this.http.get<any>(`${environment.whatsAppUrl}WhatsappTemplate/template/${id}`);
  }

  getFlowResponseForExport(id){
    return this.http.get<any>(`${environment.whatsAppUrl}WhatsappCampaign/ExportData/${id}`);
  }

  getSavedFlowResponseForExport(folderName: string,fileName: string,extension: string, file: File){
    const formData = new FormData();
    formData.append('File', file);
    return this.http.post<any>(`${environment.integrationMisc}Export/ToMyDocument?FolderName=${folderName}&FileName=${encodeURIComponent(fileName)}&FileExtension=${extension}`,formData);
  }
}

export interface WhatsAppSentMessage {
  recipientPhoneNo: string;
  phoneNoId: string;
  body: string;
}

export interface WhatsAppSentMessageBody {
  previewUrl: boolean;
  body: string;
}

export interface WhatsAppMessage {
  id: number;
  type: string;
  text: string;
  time: string;
}

export interface WhatsAppSentTemplateMessage {
  messagingProduct: string;
  to: string;
  type: string;
  template: WhatsAppTemplate;
}

export interface WhatsAppTemplate {
  name: string;
  language: {
    code: string;
  };
}

export interface WADocument {
  mediaId: string;
  blobFileLink: string;
  mimeType: string;
  fileName?: string;
  id: number;
}

export interface WhatsAppChatFilter {
  PageNumber: any;
  PageSize: any;
  LastMessageDateTimeStart: any;
  LastMessageDateTimeEnd:any
}

export interface ContactPermission {
  id: number;
  contactTypeId: number;
  userIds: number[];
  defaultContactTypeId: boolean;
  statusId: number;
}

